import React, { useState } from 'react';
import * as yup from 'yup';
import {
  Box,
  PasswordInput,
  Button,
  Stack,
  Title,
  Container,
  Group,
} from '@mantine/core';
import { changePassword } from '../services/api';
import { ChangePasswordInputModel } from '../types/models';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import { useApp } from '../context/AppContext';
import { primaryColor } from '../constants/theme';
import { ChangePasswordModal } from '../components/modals/ChangePasswordModal';

const passwordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required('Old password is required.')
    .min(8, 'Password must be at least 8 characters.')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .matches(/\d/, 'Password must contain at least one number.')
    .matches(
      /[@$!%*?&#]/,
      'Password must contain at least one special character.'
    ),
  newPassword: yup
    .string()
    .required('New password is required.')
    .min(8, 'Password must be at least 8 characters.')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .matches(/\d/, 'Password must contain at least one number.')
    .matches(
      /[@$!%*?&#]/,
      'Password must contain at least one special character.'
    )
    .notOneOf(
      [yup.ref('currentPassword')],
      'New password must not match the old password.'
    ),
  confirmPassword: yup
    .string()
    .required('Confirm password is required.')
    .oneOf(
      [yup.ref('newPassword')],
      'New password and confirm password must match.'
    ),
});

export const ChangePassword: React.FC = () => {
  const { isTabletAndBelow, scrollAreaHeight } = useApp();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const handleChangePasswordClick = async () => {
    setCurrentPasswordError('');
    setNewPasswordError('');
    setConfirmPasswordError('');

    try {
      await passwordSchema.validate(
        { currentPassword, newPassword, confirmPassword },
        { abortEarly: false }
      );
    } catch (validationErrors) {
      if (validationErrors instanceof yup.ValidationError) {
        validationErrors.inner.forEach((error) => {
          if (error.path === 'currentPassword') {
            setCurrentPasswordError(error.message);
          } else if (error.path === 'newPassword') {
            setNewPasswordError(error.message);
          } else if (error.path === 'confirmPassword') {
            setConfirmPasswordError(error.message);
          }
        });
        return;
      }
    }

    setIsLoading(true);
    const token = localStorage.getItem('token') || '';

    const changePasswordInput: ChangePasswordInputModel = {
      currentPassword,
      newPassword,
    };

    try {
      const outcome: boolean = await changePassword(changePasswordInput, token);
      if (outcome === false) {
        setCurrentPasswordError('Old password is incorrect.');
        return;
      }
      setIsSuccess(true);
      setModalTitle('Success');
      setModalMessage('Your password has been successfully changed.');
      setModalOpen(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setIsSuccess(false);
      setModalTitle('Error');
      setModalMessage(
        'There was an error changing your password. Please try again.'
      );
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setModalOpen(false);
  };

  const isFormValid = currentPassword && newPassword && confirmPassword;

  return (
    <NavigationWrapper>
      <Container px={{ base: 0, sm: 'lg' }}>
        <Box
          style={{
            backgroundColor: '#fff',
            padding: isTabletAndBelow
              ? 'var(--mantine-spacing-md)'
              : 'var(--mantine-spacing-lg)',
            boxShadow: '0 1px 8px rgba(0, 0, 0, 0.1)',
            minHeight: isTabletAndBelow ? scrollAreaHeight : 'auto',
          }}
        >
          <Title order={3} fw="normal" mb="lg">
            Change Password
          </Title>

          <Stack gap="lg">
            <PasswordInput
              label="Old Password"
              placeholder="Enter your current password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setCurrentPasswordError('');
              }}
              required
              error={currentPasswordError}
              autoComplete="current-password"
              data-testid="current-password-input"
            />

            <PasswordInput
              label="New Password"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setNewPasswordError('');
              }}
              required
              error={newPasswordError}
              autoComplete="new-password"
              data-testid="new-password-input"
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmPasswordError('');
              }}
              required
              error={confirmPasswordError}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />

            <Group justify="flex-start" mt="lg">
              <Button
                fullWidth
                onClick={handleChangePasswordClick}
                disabled={isLoading || !isFormValid}
                loading={isLoading}
                color={primaryColor}
                data-testid="change-password-button"
              >
                Change Password
              </Button>
            </Group>
          </Stack>
        </Box>

        <ChangePasswordModal
          opened={modalOpen}
          onClose={handleDialogClose}
          title={modalTitle}
          message={modalMessage}
          isSuccess={isSuccess}
        />
      </Container>
    </NavigationWrapper>
  );
};

export default ChangePassword;
